import { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { jwtDecode } from "jwt-decode";

import UseHostedCheckoutV2 from './useHostedPaymentCheckoutV2';
import usePaymentLink from '../paymentLink/usePaymentLink';
import UTILS from '../../common/utils';
import { PaymentMethodType } from '../../common/constants';
import useApplePay from '../../hooks/apple-pay';
import CardDetailsForm from '../common/cardDetailsForm';
import Wallet from '../common/wallet';
import CheckoutView from './checkoutView';
import { PaymentMethodOptions } from '../common/paymentMethodOptions';
import LoadingView from '../loadingView';
import AuthenticationView from '../AuthenticationView';

const HostedPaymentCheckoutV2 = () => {
    const [isPaymentDataValid, setIsPaymentDataValid] = useState(false);

    const paymentDataRef = useRef({});
    const selectedPaymentMethodRef = useRef();

    const isApplePayEnabled = useApplePay();

    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const sessionId = searchParams.get('session_id');

    const getIdFromToken = (token) => {
        const decoded = jwtDecode(token);
        return decoded.unique_name;
    }

    const { sessionInfo, merchantName, isFetchingSessionInfo } = UseHostedCheckoutV2(token, sessionId);
    const { submitPayment, isPaying, merchantLogo, showAuthView, authViewHtml, authViewUrl, isLoading } = 
        usePaymentLink(null, null, getIdFromToken(token));

    useEffect(() => {
        if (!sessionInfo) return;

        onChangeSelectedPaymentMethod(
            sessionInfo.payment_method_type === PaymentMethodType.Unknown ?
                PaymentMethodType.BankCard : sessionInfo.payment_method_type);
    }, [sessionInfo]);

    const handlePaymentDataChange = (field, value) => {
        paymentDataRef.current[field] = value;
        setIsPaymentDataValid(UTILS.ValidateFormData(paymentDataRef.current, selectedPaymentMethodRef.current));
    };

    const onChangeSelectedPaymentMethod = (method) => {
        selectedPaymentMethodRef.current = method;
        paymentDataRef.current = {};
        setIsPaymentDataValid(UTILS.ValidateFormData(paymentDataRef.current, selectedPaymentMethodRef.current));
    };

    const onSubmit = async () => {
        const failUrl = sessionInfo.failure_url;
        const paymentSession = { authToken: token, sessionId, failUrl };
        await submitPayment(
            paymentSession,
            paymentDataRef.current,
            selectedPaymentMethodRef.current,
            merchantName,
            sessionInfo.is_3ds_required,
        );
    }

    const getPaymentMethodComponent = () => {
        switch (sessionInfo.payment_method_type) {
            case PaymentMethodType.BankCard:
                return <CardDetailsForm 
                    handleChange={handlePaymentDataChange}
                    isSaveCardOptionEnabled={sessionInfo.is_save_card_checkbox_enabled}
                    isCVVRequired={sessionInfo.is_cvv_required} />
            case PaymentMethodType.Wallet:
                return <Wallet handleChange={handlePaymentDataChange} />
            case PaymentMethodType.Unknown:
                return <PaymentMethodOptions
                            isCardEnabled={sessionInfo.is_bank_card_enabled}
                            isWalletEnabled={sessionInfo.is_wallet_enabled}
                            isApplePayEnabled={isApplePayEnabled}
                            isSaveCardOptionEnabled={sessionInfo.is_save_card_checkbox_enabled}
                            handleChange={handlePaymentDataChange}
                            onChangePaymentMethod={onChangeSelectedPaymentMethod}
                        />
            default:
                return <></>
        }
    }

    const getTitle = () => {
        switch (sessionInfo.payment_method_type) {
            case PaymentMethodType.BankCard:
                return "Enter card details";
            case PaymentMethodType.Wallet:
                return "Enter your wallet number";
            default:
                return "";
        }
    }

    if ((isFetchingSessionInfo || isLoading) && !showAuthView)
        return (<LoadingView color='#4055FF'/>);

    if (showAuthView) {
        return (
            <AuthenticationView
                paymentMethodType={selectedPaymentMethodRef.current}
                authViewHtml={authViewHtml}
                authViewUrl={authViewUrl} />);
    }

    return (
        <CheckoutView
            onSubmit={onSubmit} 
            isButtonLoading={isPaying}
            isAllFieldsValid={isPaymentDataValid}
            buttonLabel="Pay"
            merchantLogo={merchantLogo}
            title={getTitle()}>
            {getPaymentMethodComponent()}
        </CheckoutView>
    );

}

export default HostedPaymentCheckoutV2;